import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import QRCode from "react-qr-code";
import { useAuth, useTheme, API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { toast } from "sonner";
import axios from "axios";
import {
  User, Mail, Phone, MapPin, Briefcase, Globe, Linkedin, 
  QrCode, Share2, LogOut, Moon, Sun, Upload, ExternalLink, Copy, Eye, TrendingUp, BarChart3,
  Instagram, Facebook, Twitter, Youtube, AtSign, Lock, Ghost, Music2
} from "lucide-react";
import WhatsAppButton from "@/components/WhatsAppButton";

const UserDashboard = () => {
  const navigate = useNavigate();
  const { user, token, logout, updateUser } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const [profile, setProfile] = useState(user);
  const [landingSettings, setLandingSettings] = useState({});
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [showQR, setShowQR] = useState(false);
  const [company, setCompany] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [pwDialogOpen, setPwDialogOpen] = useState(false);
  const [pwForm, setPwForm] = useState({ current_password: "", new_password: "", confirm_password: "" });
  const [pwLoading, setPwLoading] = useState(false);
  const fileInputRef = useRef(null);

  const authHeader = { headers: { Authorization: `Bearer ${token}` } };
  const profileUrl = `${window.location.origin}/profile/${profile?.slug}`;
  const isCompanyUser = profile?.role === "company_user";

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [profileRes, settingsRes] = await Promise.all([
        axios.get(`${API}/users/me`, authHeader),
        axios.get(`${API}/landing-settings`)
      ]);
      setProfile(profileRes.data);
      setLandingSettings(settingsRes.data);
      
      // Fetch company if user belongs to one
      if (profileRes.data.company_id) {
        const companyRes = await axios.get(`${API}/companies/${profileRes.data.company_id}`, authHeader);
        setCompany(companyRes.data);
      }
      
      // Fetch analytics
      try {
        const analyticsRes = await axios.get(`${API}/analytics/profile/${profileRes.data.id}`, authHeader);
        setAnalytics(analyticsRes.data);
      } catch (e) {
        // Analytics optional — ignore if unavailable
      }
    } catch (error) {
      // Failed to load dashboard data
    }
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      const response = await axios.put(`${API}/users/me`, {
        name: profile.name,
        profession: profile.profession,
        profession_details: profile.profession_details,
        phone: profile.phone,
        whatsapp: profile.whatsapp,
        linkedin: profile.linkedin,
        instagram: profile.instagram,
        facebook: profile.facebook,
        twitter: profile.twitter,
        tiktok: profile.tiktok,
        snapchat: profile.snapchat,
        youtube: profile.youtube,
        email_public: profile.email_public,
        bio: profile.bio,
        location: profile.location,
        website: profile.website,
        theme: profile.theme
      }, authHeader);
      updateUser(response.data);
      setProfile(response.data);
      toast.success("Profile updated successfully");
    } catch (error) {
      toast.error("Failed to update profile");
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async () => {
    if (pwForm.new_password.length < 6) {
      toast.error("New password must be at least 6 characters");
      return;
    }
    if (pwForm.new_password !== pwForm.confirm_password) {
      toast.error("New password and confirmation do not match");
      return;
    }
    setPwLoading(true);
    try {
      await axios.post(`${API}/auth/change-password`, {
        current_password: pwForm.current_password,
        new_password: pwForm.new_password
      }, authHeader);
      toast.success("Password updated successfully");
      setPwForm({ current_password: "", new_password: "", confirm_password: "" });
      setPwDialogOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update password");
    } finally {
      setPwLoading(false);
    }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    const formData = new FormData();
    formData.append("file", file);

    try {
      const response = await axios.post(`${API}/upload/photo`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data"
        }
      });
      
      // Update profile with new photo
      const updateRes = await axios.put(`${API}/users/me`, {
        photo_url: response.data.photo_url
      }, authHeader);
      
      setProfile(updateRes.data);
      updateUser(updateRes.data);
      toast.success("Photo uploaded successfully");
    } catch (error) {
      toast.error("Failed to upload photo");
    } finally {
      setUploading(false);
    }
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(profileUrl);
    toast.success("Profile link copied!");
  };

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  // Determine if user can change theme (individual users only)
  const canChangeTheme = !profile?.company_id;

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-card border-b p-4">
        <div className="container mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Avatar className="h-10 w-10">
              <AvatarImage 
                src={profile?.photo_url ? (profile.photo_url.startsWith("/") ? `${process.env.REACT_APP_BACKEND_URL}${profile.photo_url}` : profile.photo_url) : undefined} 
              />
              <AvatarFallback>{profile?.name?.charAt(0) || "?"}</AvatarFallback>
            </Avatar>
            <div>
              <h2 className="font-semibold">{profile?.name}</h2>
              <p className="text-xs text-muted-foreground">{profile?.profession || "Add your profession"}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {canChangeTheme && (
              <Button variant="ghost" size="icon" onClick={toggleTheme} data-testid="theme-toggle">
                {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
              </Button>
            )}
            {profile?.role === "company_admin" && (
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => navigate("/company-admin")}
                data-testid="company-admin-btn"
              >
                <Briefcase className="h-4 w-4 mr-2" /> Company Admin
              </Button>
            )}
            <Button variant="ghost" size="icon" onClick={handleLogout} data-testid="logout-btn">
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-6 pb-24 max-w-2xl">
        {/* Company Badge */}
        {company && (
          <div 
            className="mb-6 p-4 rounded-xl flex items-center gap-3"
            style={{ 
              backgroundColor: company.primary_color + "10",
              borderLeft: `4px solid ${company.primary_color}`
            }}
          >
            {company.logo_url && (
              <img 
                src={company.logo_url.startsWith("/") ? `${process.env.REACT_APP_BACKEND_URL}${company.logo_url}` : company.logo_url}
                alt={company.name}
                className="h-8 w-auto"
              />
            )}
            <div>
              <p className="text-sm font-medium">{company.name}</p>
              <p className="text-xs text-muted-foreground">Company theme applied to your profile</p>
            </div>
          </div>
        )}

        {/* Quick Actions */}
        <div className="grid grid-cols-2 gap-3 mb-6">
          <Dialog open={showQR} onOpenChange={setShowQR}>
            <DialogTrigger asChild>
              <Button variant="outline" className="h-auto py-4 flex-col gap-2" data-testid="show-qr-btn">
                <QrCode className="h-6 w-6" />
                <span>Show QR</span>
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-sm">
              <DialogHeader>
                <DialogTitle className="text-center">Your QR Code</DialogTitle>
              </DialogHeader>
              <div className="flex flex-col items-center py-6">
                <div className="qr-container">
                  <QRCode value={profileUrl} size={200} />
                </div>
                <p className="text-sm text-muted-foreground mt-4 text-center">
                  Scan this code to view your profile
                </p>
                <Button variant="outline" className="mt-4" onClick={copyToClipboard}>
                  <Copy className="h-4 w-4 mr-2" /> Copy Link
                </Button>
              </div>
            </DialogContent>
          </Dialog>

          <Button 
            variant="outline" 
            className="h-auto py-4 flex-col gap-2"
            onClick={() => window.open(profileUrl, "_blank")}
            data-testid="view-profile-btn"
          >
            <ExternalLink className="h-6 w-6" />
            <span>View Profile</span>
          </Button>
        </div>

        {/* Analytics Section */}
        {analytics && (
          <motion.div 
            initial={{ opacity: 0, y: 20 }} 
            animate={{ opacity: 1, y: 0 }}
            className="mb-6"
          >
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2">
                  <BarChart3 className="h-5 w-5" /> Profile Analytics
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-3 gap-4 mb-4">
                  <div className="text-center p-3 bg-muted rounded-lg">
                    <div className="flex items-center justify-center gap-1 text-2xl font-bold">
                      <Eye className="h-5 w-5 text-muted-foreground" />
                      {analytics.total_views}
                    </div>
                    <p className="text-xs text-muted-foreground">Total Views</p>
                  </div>
                  <div className="text-center p-3 bg-muted rounded-lg">
                    <div className="flex items-center justify-center gap-1 text-2xl font-bold text-green-600">
                      <TrendingUp className="h-5 w-5" />
                      {analytics.views_last_7_days}
                    </div>
                    <p className="text-xs text-muted-foreground">Last 7 Days</p>
                  </div>
                  <div className="text-center p-3 bg-muted rounded-lg">
                    <div className="flex items-center justify-center gap-1 text-2xl font-bold text-blue-600">
                      {analytics.views_last_30_days}
                    </div>
                    <p className="text-xs text-muted-foreground">Last 30 Days</p>
                  </div>
                </div>
                
                {/* Daily views chart */}
                {analytics.daily_views && analytics.daily_views.length > 0 && (
                  <div className="mt-4">
                    <p className="text-xs text-muted-foreground mb-2">Views this week</p>
                    <div className="flex items-end gap-1 h-16">
                      {analytics.daily_views.map((day, idx) => {
                        const maxViews = Math.max(...analytics.daily_views.map(d => d.views), 1);
                        const height = day.views > 0 ? Math.max((day.views / maxViews) * 100, 10) : 5;
                        return (
                          <div key={day.date || idx} className="flex-1 flex flex-col items-center gap-1">
                            <div 
                              className="w-full bg-primary/80 rounded-t transition-all"
                              style={{ height: `${height}%` }}
                              title={`${day.date}: ${day.views} views`}
                            />
                            <span className="text-[10px] text-muted-foreground">
                              {new Date(day.date).toLocaleDateString('en', { weekday: 'short' }).charAt(0)}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* Profile Form */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <Card>
            <CardHeader>
              <CardTitle>Edit Profile</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Photo Upload */}
              <div className="flex items-center gap-4">
                <Avatar className="h-20 w-20">
                  <AvatarImage 
                    src={profile?.photo_url ? (profile.photo_url.startsWith("/") ? `${process.env.REACT_APP_BACKEND_URL}${profile.photo_url}` : profile.photo_url) : undefined}
                  />
                  <AvatarFallback className="text-2xl">{profile?.name?.charAt(0) || "?"}</AvatarFallback>
                </Avatar>
                <div>
                  <input 
                    type="file" 
                    ref={fileInputRef}
                    className="hidden"
                    accept="image/*"
                    onChange={handlePhotoUpload}
                  />
                  <Button 
                    variant="outline" 
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                    data-testid="upload-photo-btn"
                  >
                    <Upload className="h-4 w-4 mr-2" />
                    {uploading ? "Uploading..." : "Change Photo"}
                  </Button>
                </div>
              </div>

              {/* Basic Info */}
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Full Name</Label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="name"
                      data-testid="profile-name-input"
                      className="pl-10"
                      value={profile?.name || ""}
                      onChange={(e) => setProfile({ ...profile, name: e.target.value })}
                      disabled={isCompanyUser}
                    />
                  </div>
                  {isCompanyUser && (
                    <p className="text-xs text-muted-foreground" data-testid="name-locked-note">
                      Managed by your company
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="profession">Profession</Label>
                  <div className="relative">
                    <Briefcase className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="profession"
                      data-testid="profile-profession-input"
                      className="pl-10"
                      placeholder="e.g., Software Engineer"
                      value={profile?.profession || ""}
                      onChange={(e) => setProfile({ ...profile, profession: e.target.value })}
                      disabled={isCompanyUser}
                    />
                  </div>
                  {isCompanyUser && (
                    <p className="text-xs text-muted-foreground" data-testid="profession-locked-note">
                      Managed by your company
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="profession_details">Profession Details</Label>
                  <Textarea
                    id="profession_details"
                    data-testid="profile-profession-details-input"
                    placeholder="e.g., Specializing in React and Node.js"
                    rows={2}
                    value={profile?.profession_details || ""}
                    onChange={(e) => setProfile({ ...profile, profession_details: e.target.value })}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="bio">Bio</Label>
                  <Textarea
                    id="bio"
                    data-testid="profile-bio-input"
                    placeholder="Tell people about yourself..."
                    rows={3}
                    value={profile?.bio || ""}
                    onChange={(e) => setProfile({ ...profile, bio: e.target.value })}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="location">Location</Label>
                  <div className="relative">
                    <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="location"
                      data-testid="profile-location-input"
                      className="pl-10"
                      placeholder="e.g., New York, USA"
                      value={profile?.location || ""}
                      onChange={(e) => setProfile({ ...profile, location: e.target.value })}
                    />
                  </div>
                </div>
              </div>

              {/* Contact Info */}
              <div className="border-t pt-6">
                <h3 className="font-medium mb-4">Contact Information</h3>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="email_public">Public Email</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="email_public"
                        data-testid="profile-email-input"
                        type="email"
                        className="pl-10"
                        value={profile?.email_public || ""}
                        onChange={(e) => setProfile({ ...profile, email_public: e.target.value })}
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="phone">Phone</Label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="phone"
                        data-testid="profile-phone-input"
                        className="pl-10"
                        placeholder="+1 234 567 8900"
                        value={profile?.phone || ""}
                        onChange={(e) => setProfile({ ...profile, phone: e.target.value })}
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="whatsapp">WhatsApp</Label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="whatsapp"
                        data-testid="profile-whatsapp-input"
                        className="pl-10"
                        placeholder="+1 234 567 8900"
                        value={profile?.whatsapp || ""}
                        onChange={(e) => setProfile({ ...profile, whatsapp: e.target.value })}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Social Links */}
              <div className="border-t pt-6">
                <h3 className="font-medium mb-4">Social Links</h3>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="linkedin">LinkedIn</Label>
                    <div className="relative">
                      <Linkedin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="linkedin"
                        data-testid="profile-linkedin-input"
                        className="pl-10"
                        placeholder="https://linkedin.com/in/yourprofile"
                        value={profile?.linkedin || ""}
                        onChange={(e) => setProfile({ ...profile, linkedin: e.target.value })}
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="instagram">Instagram</Label>
                    <div className="relative">
                      <Instagram className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="instagram"
                        data-testid="profile-instagram-input"
                        className="pl-10"
                        placeholder="https://instagram.com/yourhandle"
                        value={profile?.instagram || ""}
                        onChange={(e) => setProfile({ ...profile, instagram: e.target.value })}
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="facebook">Facebook</Label>
                    <div className="relative">
                      <Facebook className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="facebook"
                        data-testid="profile-facebook-input"
                        className="pl-10"
                        placeholder="https://facebook.com/yourpage"
                        value={profile?.facebook || ""}
                        onChange={(e) => setProfile({ ...profile, facebook: e.target.value })}
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="twitter">X (Twitter)</Label>
                    <div className="relative">
                      <Twitter className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="twitter"
                        data-testid="profile-twitter-input"
                        className="pl-10"
                        placeholder="https://x.com/yourhandle"
                        value={profile?.twitter || ""}
                        onChange={(e) => setProfile({ ...profile, twitter: e.target.value })}
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="tiktok">TikTok</Label>
                    <div className="relative">
                      <Music2 className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="tiktok"
                        data-testid="profile-tiktok-input"
                        className="pl-10"
                        placeholder="https://tiktok.com/@yourhandle"
                        value={profile?.tiktok || ""}
                        onChange={(e) => setProfile({ ...profile, tiktok: e.target.value })}
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="snapchat">Snapchat</Label>
                    <div className="relative">
                      <Ghost className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="snapchat"
                        data-testid="profile-snapchat-input"
                        className="pl-10"
                        placeholder="https://snapchat.com/add/yourhandle"
                        value={profile?.snapchat || ""}
                        onChange={(e) => setProfile({ ...profile, snapchat: e.target.value })}
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="youtube">YouTube</Label>
                    <div className="relative">
                      <Youtube className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="youtube"
                        data-testid="profile-youtube-input"
                        className="pl-10"
                        placeholder="https://youtube.com/@yourchannel"
                        value={profile?.youtube || ""}
                        onChange={(e) => setProfile({ ...profile, youtube: e.target.value })}
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="website">Website</Label>
                    <div className="relative">
                      <Globe className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="website"
                        data-testid="profile-website-input"
                        className="pl-10"
                        placeholder="https://yourwebsite.com"
                        value={profile?.website || ""}
                        onChange={(e) => setProfile({ ...profile, website: e.target.value })}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Theme (Individual users only) */}
              {canChangeTheme && (
                <div className="border-t pt-6">
                  <h3 className="font-medium mb-4">Profile Theme</h3>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm">Dark Mode</p>
                      <p className="text-xs text-muted-foreground">Apply dark theme to your public profile</p>
                    </div>
                    <Switch
                      checked={profile?.theme === "dark"}
                      onCheckedChange={(v) => setProfile({ ...profile, theme: v ? "dark" : "light" })}
                      data-testid="profile-theme-switch"
                    />
                  </div>
                </div>
              )}

              {/* Security - Change Password */}
              <div className="border-t pt-6">
                <h3 className="font-medium mb-4">Security</h3>
                <Dialog open={pwDialogOpen} onOpenChange={setPwDialogOpen}>
                  <DialogTrigger asChild>
                    <Button variant="outline" className="w-full" data-testid="open-change-password-btn">
                      <Lock className="h-4 w-4 mr-2" /> Change Password
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-sm">
                    <DialogHeader>
                      <DialogTitle>Change Password</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-2">
                      <div className="space-y-2">
                        <Label htmlFor="current_password">Current Password</Label>
                        <Input
                          id="current_password"
                          type="password"
                          data-testid="current-password-input"
                          value={pwForm.current_password}
                          onChange={(e) => setPwForm({ ...pwForm, current_password: e.target.value })}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="new_password">New Password</Label>
                        <Input
                          id="new_password"
                          type="password"
                          data-testid="new-password-input"
                          placeholder="At least 6 characters"
                          value={pwForm.new_password}
                          onChange={(e) => setPwForm({ ...pwForm, new_password: e.target.value })}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="confirm_password">Confirm New Password</Label>
                        <Input
                          id="confirm_password"
                          type="password"
                          data-testid="confirm-password-input"
                          value={pwForm.confirm_password}
                          onChange={(e) => setPwForm({ ...pwForm, confirm_password: e.target.value })}
                        />
                      </div>
                      <Button
                        onClick={handleChangePassword}
                        className="w-full"
                        disabled={pwLoading}
                        data-testid="submit-change-password-btn"
                      >
                        {pwLoading ? "Updating..." : "Update Password"}
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>
              </div>

              <Button 
                onClick={handleSave} 
                className="w-full"
                disabled={loading}
                data-testid="save-profile-btn"
              >
                {loading ? "Saving..." : "Save Changes"}
              </Button>
            </CardContent>
          </Card>
        </motion.div>
      </main>

      <WhatsAppButton phone={landingSettings?.support_whatsapp} />
    </div>
  );
};

export default UserDashboard;
